import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useFullScreenLoader } from '../../../contexts/fullScreenLoader';
import { useToasts } from '../../../contexts/toasts';
import { Link, useParams } from 'react-router-dom';
import { ControlPanelManager } from '../../../api/ControlPanelManager';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Alert, Flex, Form, Modal, Select, Space, Table, Tooltip } from 'antd';
import { Breadcrumb, Button, Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import IconButton from '../../../components/common/IconButton';
import { Organization } from '../../../models/ControlPanel';
import { User } from '../../../models/User';
import AppLoader from '../../../components/common/AppLoader';

export default function OrganizationMembers() {
  const { id } = useParams();
  const [group, setGroup] = useState<Organization>();
  const [user, setUser] = useState<User>();
  const [users, setUsers] = useState<User[]>();
  const [members, setMembers] = useState<User[]>();
  const [error, setError] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  const { showLoader, hideLoader } = useFullScreenLoader();
  const { showToast } = useToasts();

  const usersOptions = useMemo(() => {
    const remainingUsers = users?.filter(u => !members?.map(m => m.id).includes(u.id));
    return remainingUsers?.map(u => ({ label: u.username, value: u.id }));
  }, [users, members]);

  const initGroup = useCallback(() => {
    ControlPanelManager.getGroup(id!)
      .then(
        ({ data }) => setGroup(data),
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('group-error', message, `Group Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [id, showToast]);

  const getGroupMembers = useCallback(() => {
    ControlPanelManager.getGroupMembers(id!)
      .then(
        ({ data }) => setMembers(data),
        (err) => {
          setMembers([]);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('members-error', message, `Members Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [id, showToast]);

  const getUsers = useCallback(() => {
    ControlPanelManager.getUsers()
      .then(
        ({ data }) => setUsers(data),
        (err) => {
          setUsers([]);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('users-error', message, `Users Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showToast]);

  useEffect(() => {
    if (id) {
      initGroup();
      getGroupMembers();
      getUsers();
    }
  }, [id, initGroup, getGroupMembers, getUsers]);

  const reset = useCallback(() => {
    setMembers(undefined);
    getGroupMembers();
  }, [getGroupMembers]);

  const openAddModal = () => setShowAddModal(true);
  const handleAddModalClose = () => setShowAddModal(false);
  const openRemoveModal = (user: User) => () => {
    setUser(user);
    setShowRemoveModal(true);
  }
  const handleRemoveModalClose = () => {
    setUser(undefined);
    setShowRemoveModal(false);
  }

  const addUser = ({ userId }: { userId: string }) => {
    showLoader();

    ControlPanelManager.addUserToGroup(userId, id!)
      .then(
        () => {
          hideLoader();
          showToast('user-success', 'User added successfully', 'User Added', 'success');
          handleAddModalClose();
          reset();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('user-error', message, `User Removal Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const removeUser = () => {
    showLoader();

    ControlPanelManager.removeUserFromGroup(user!.id!, id!)
      .then(
        () => {
          hideLoader();
          showToast('user-success', 'User removed successfully', 'User Removed', 'success');
          handleRemoveModalClose();
          reset();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('user-error', message, `User Removal Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const renderAsyncContent = useCallback(() => {
    if (error) return <Alert type="error" showIcon message="An error occurred, please try again later" />

    if (!group) return <AppLoader className="my-5 py-5" justify="center" size={100} />;

    return (
      <>
        <Flex align="center" justify="space-between" className="mb-3">
          <h5 className="m-0">{group?.name} Members</h5>

          <Button variant="success" size="sm" onClick={openAddModal}>
            <FontAwesomeIcon icon={solid('user-plus')} />
            <span className="ms-2">Add Member</span>
          </Button>
        </Flex>

        <Table size="middle" dataSource={members} loading={!members} rowKey="id" scroll={{ y: 'calc(100vh - 300px)' }}>
          <Table.Column
            key="username"
            dataIndex="username"
            title="Username"
            render={(value, record: User) => <Link to={`/control-panel/users-management/${record.id}`}>{value}</Link>}
          />
          <Table.Column
            key="email"
            dataIndex="email"
            title="Email"
            render={(value, record: User) => (
              <>
                {(value && !record.emailVerified) && (
                  <Tooltip title="Email not verified">
                    <FontAwesomeIcon className="text-warning me-2" icon={solid('exclamation-circle')} />
                  </Tooltip>
                )}
                <span className="text-muted">{value || '--'}</span>
              </>
            )}
          />
          <Table.Column
            key="actions"
            title="Actions"
            className="text-center"
            width="12rem"
            render={(_value, record: User) => (
              <Space size={1}>
                <IconButton tooltip="Remove" className="text-danger ms-2" onClick={openRemoveModal(record)}>
                  <FontAwesomeIcon fixedWidth icon={solid('user-times')} />
                </IconButton>
              </Space>
            )}
          />
        </Table>
      </>
    )
  }, [error, group, members]);

  return (
    <Container className="py-3">
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/control-panel' }}>Control Panel</Breadcrumb.Item>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/control-panel/organizations-management' }}>Organization Management</Breadcrumb.Item>
        <Breadcrumb.Item active>{group?.name} Members</Breadcrumb.Item>
      </Breadcrumb>

      {renderAsyncContent()}

      <Modal
        title="Remove User"
        open={showRemoveModal}
        onCancel={handleRemoveModalClose}
        afterClose={handleRemoveModalClose}
        footer={
          <Flex justify="space-between" className="mt-3">
            <Button size="sm" variant="light" className="border" onClick={handleRemoveModalClose}>Cancel</Button>
            <Button size="sm" variant="danger" onClick={removeUser}>
              <FontAwesomeIcon icon={solid('times')} />
              <span className="ms-2">Confirm</span>
            </Button>
          </Flex>
        }
      >
        <p>Are you sure you want to remove user <b>{user?.username}</b> from <b>{group?.name}</b>?</p>
      </Modal>

      <Modal
        title="Add User"
        open={showAddModal}
        onCancel={handleAddModalClose}
        afterClose={handleAddModalClose}
        footer={null}
      >
        <Form layout="vertical" onFinish={addUser}>
          <Form.Item label="User" name="userId" rules={[{ required: true, message: 'You need to select the user before proceeding' }]}>
            <Select placeholder="Select user" options={usersOptions} loading={!usersOptions} />
          </Form.Item>

          <br />

          <Flex justify="space-between">
            <Button size="sm" type="button" variant="light" className="border" onClick={handleAddModalClose}>Cancel</Button>
            <Button size="sm" type="submit" variant="success">
              <FontAwesomeIcon icon={solid('check')} />
              <span className="ms-2">Confirm</span>
            </Button>
          </Flex>
        </Form>
      </Modal>
    </Container>
  )
}
